import React from 'react'
import { HelpTooltip } from 'components/Tooltip/HelpTooltip'
import { cn } from 'components/UI/cn'

interface MetricCardProps {
  /** Icon element displayed in the leading square. */
  icon: React.ReactNode
  /** Formatted metric value (e.g., "1,234" or "65.5%"). */
  value: string
  /** Short label describing the metric. */
  label: string
  /** Detailed description shown in the tooltip popup; omit to hide the help icon. */
  tooltipContent?: string
  /** Extra classes for the card container (e.g. `border-0` to drop the stroke).
   *  Merged last via tailwind-merge so callers can override the defaults. */
  className?: string
}

/**
 * DS2026 compact metric card. Icon square (light brand tint) on the left,
 * label + optional help tooltip on top, large bold value below. Used on the
 * Performance Dashboard and any page-header stat tiles.
 */
export const MetricCard: React.FC<MetricCardProps> = ({
  icon,
  value,
  label,
  tooltipContent,
  className,
}) => (
  <div
    className={cn(
      'flex items-center gap-3 bg-surface rounded-lg border border-line p-3.5',
      className
    )}
  >
    <div className="size-9 rounded-lg bg-brand-active/10 flex items-center justify-center shrink-0 text-brand-active">
      {icon}
    </div>
    <div>
      <div className="flex items-center gap-1 whitespace-nowrap text-sm text-fg-secondary">
        {label}
        {tooltipContent && (
          <HelpTooltip content={tooltipContent} placement="top" />
        )}
      </div>
      <div className="text-lg font-bold text-fg leading-tight tabular-nums">
        {value}
      </div>
    </div>
  </div>
)
